import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { EyeIcon, PencilIcon, TrashBinIcon } from "../../icons"

type CertificateTemplate = {
  id: string
  name: string
  background_url: string
  canvasWidth: number
  canvasHeight: number
  mode: "individual" | "group"
  seedbedId: string | null
  createdAt: string
}

export default function CertificateTemplateListPage() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<CertificateTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<CertificateTemplate | null>(null)
  const [search, setSearch] = useState("")
  const [modeFilter, setModeFilter] = useState<"" | "individual" | "group">("")  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get("/academy/certificate-templates")
      setTemplates(res.data.data ?? [])
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function handleDelete(id: string) {
    setDeletingId(id)
    try {
      await api.delete(`/academy/certificate-templates/${id}`)
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setDeletingId(null)
      setTemplateToDelete(null)
    }
  }

  const filtered = useMemo(() => {
    let result = templates
    if (modeFilter) result = result.filter((t) => t.mode === modeFilter)
    if (search.trim()) {
      const term = search.toLowerCase()
      result = result.filter((t) => t.name.toLowerCase().includes(term))
    }
    return result
  }, [templates, search, modeFilter])

  const columns: DataTableColumn<CertificateTemplate>[] = [
    {
      key: "name",
      label: "Plantilla",
      render: (t) => (
        <div className="flex items-center gap-3">
          <img
            src={t.background_url}
            alt={t.name}
            className="h-10 w-14 rounded-md object-cover bg-gray-100 dark:bg-gray-800"
          />
          <p className="font-medium text-gray-900 dark:text-white">{t.name}</p>
        </div>
      ),
    },
    {
      key: "mode",
      label: "Modo",
      render: (t) => (
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            t.mode === "group"
              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
          }`}
        >
          {t.mode === "group" ? "Certificado de grupo" : "Individual"}
        </span>
      ),
    },
    {
      key: "size",
      label: "Tamaño",
      render: (t) => (
        <span className="text-xs text-gray-500">
          {t.canvasWidth}×{t.canvasHeight}px
        </span>
      ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu
            actions={[
              { label: "Abrir", icon: <EyeIcon />, to: `/instructor/certificate-templates/${t.id}` },
              { label: "Editar", icon: <PencilIcon />, to: `/instructor/certificate-templates/${t.id}`, requires: "academy:certificates:edit" },
              {
                label: deletingId === t.id ? "Eliminando..." : "Eliminar",
                icon: <TrashBinIcon />,
                onClick: () => setTemplateToDelete(t),
                variant: "danger",
                disabled: deletingId === t.id,
                requires: "academy:certificates:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Certificados", to: "/instructor/certificates" },
        "Plantillas",
      ]}
      title="Plantillas de certificado"
      subtitle="Diseña la imagen y el posicionamiento de datos de cada certificado"
      tabs={[
        { label: "Certificados", to: "/instructor/certificates" },
        { label: "Plantillas", to: "/instructor/certificate-templates", active: true },
      ]}
      metrics={[{ label: "Plantillas creadas", value: String(templates.length) }]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar plantillas...",
        },
        filters: [
          {
            type: "select",
            key: "mode",
            label: "Modo",
            value: modeFilter,
            onChange: (v) => setModeFilter(v as "" | "individual" | "group"),
            options: [
              { label: "Todos", value: "" },
              { label: "Individual", value: "individual" },
              { label: "Certificado de grupo", value: "group" },
            ],
          },
        ],
        onRefresh: load,
        refreshing: loading,        onCreate: () => navigate("/instructor/certificate-templates/new"),
        createLabel: "+ Nueva plantilla",
        requires: "academy:certificates:edit",
      }}
    >
      {error && (
        <p role="alert" aria-live="assertive" className="text-sm text-error-600 dark:text-error-400">
          {error}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(t) => t.id}
        loading={loading}
        emptyMessage="Sin plantillas aún. Crea una plantilla subiendo una imagen de fondo."
        page={1}
        pageSize={Math.max(filtered.length, 1)}
        total={filtered.length}
        onPageChange={() => {}}
      />      <ConfirmDialog
        isOpen={templateToDelete !== null}
        title={`¿Eliminar "${templateToDelete?.name ?? ""}"?`}
        message="Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirming={deletingId === templateToDelete?.id}
        onConfirm={() => templateToDelete && handleDelete(templateToDelete.id)}
        onCancel={() => setTemplateToDelete(null)}
      />
    </ModuleLayout>
  )
}
